import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {
  Button,
  CircularProgress,
  Dialog,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { APP_IMAGES } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import DialogBody from "../../../components/Dialog/Body";
import DialogActionButtons from "../../../components/Dialog/Footer";
import DialogHeader from "../../../components/Dialog/Header";
import Label from "../../../components/Label";
import TextInput from "../../../components/TextInputs";
import UploadBox from "../../../components/UploadBox";
import PackageCard from "../../../components/Cards/PackageCard";
import AttachedBasePackageDialog from "../blogs/AttachedBasePackageDialog";
import { baseUrl } from "../../../services";
import {
  createCampaign,
  updateCampaign,
} from "../../../services/campaignApiFn";
import { handleOpenSnackBar } from "../../../utils";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "750px",
    maxWidth: "750px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

export const CAMPAIGN_TYPES = ["starter_box"];
export const PLATFORMS = ["facebook", "tiktok", "instagram"];

let paramsData = {};

const MAX_PDF_SIZE = 10 * 1024 * 1024;

const emptyTier = {
  minViews: "",
  maxViews: "",
  timeFrameDays: "",
  budget: "",
};

const emptyKpi = {
  platform: "",
  tiers: [{ ...emptyTier }],
};

// Purane campaigns me range seedha KPI pe thi, unhe ek range bana dete hain
export const getKpiTiers = (kpi) =>
  kpi?.tiers?.length ? kpi.tiers : kpi?.minViews !== undefined ? [kpi] : [];

const initialInputValues = {
  name: "",
  type: "starter_box",
  instructions: "",
  coverImage: null,
  instructionFile: null,
  maxCreators: "",
  commission: "",
  kpis: [{ ...emptyKpi }],
};

// Model ki validations ko frontend par bhi check kerta hai
const validateCampaign = (values, selectedPackages, setInputError) => {
  const errors = {};

  if (!selectedPackages.length) errors.packages = "validation.starterBoxRequired";
  const kpiErrors = [];

  if (!values.name?.trim()) errors.name = "validation.nameRequired";
  else if (values.name.trim().length < 3)
    errors.name = "validation.nameMinLength";

  if (!values.type) errors.type = "validation.typeRequired";

  if (
    values.maxCreators !== "" &&
    (!Number.isInteger(Number(values.maxCreators)) ||
      Number(values.maxCreators) < 1)
  ) {
    errors.maxCreators = "validation.maxCreatorsInvalid";
  }

  if (
    values.commission === "" ||
    Number(values.commission) < 0 ||
    Number(values.commission) > 100
  ) {
    errors.commission = "validation.commissionInvalid";
  }

  if (!values.kpis.length) errors.kpisGeneral = "validation.kpiRequired";

  const platforms = values.kpis.map((k) => k.platform).filter(Boolean);
  if (new Set(platforms).size !== platforms.length)
    errors.kpisGeneral = "validation.platformDuplicate";

  values.kpis.forEach((kpi, i) => {
    const kpiError = {};
    if (!kpi.platform) kpiError.platform = "validation.platformRequired";
    if (!kpi.tiers.length) kpiError.tiersGeneral = "validation.rangeRequired";

    const tierErrors = [];
    kpi.tiers.forEach((tier, j) => {
      const tierError = {};
      if (tier.minViews === "" || Number(tier.minViews) < 0)
        tierError.minViews = "validation.minViewsRequired";
      if (tier.maxViews === "" || Number(tier.maxViews) < 0)
        tierError.maxViews = "validation.maxViewsRequired";
      if (
        !tierError.minViews &&
        !tierError.maxViews &&
        Number(tier.minViews) > Number(tier.maxViews)
      )
        tierError.minViews = "validation.minGreaterThanMax";
      if (tier.timeFrameDays === "" || Number(tier.timeFrameDays) < 1)
        tierError.timeFrameDays = "validation.timeFrameRequired";
      if (tier.budget === "" || Number(tier.budget) < 0)
        tierError.budget = "validation.budgetRequired";
      if (Object.keys(tierError).length) tierErrors[j] = tierError;
    });
    if (tierErrors.length) kpiError.tiers = tierErrors;

    if (Object.keys(kpiError).length) kpiErrors[i] = kpiError;
  });

  if (kpiErrors.length) errors.kpis = kpiErrors;

  setInputError(errors);
  return Object.keys(errors).length > 0;
};

const AddEditCampaign = forwardRef(({}, ref) => {
  const coverImageRef = React.useRef(null);
  const instructionFileRef = React.useRef(null);
  const starterBoxDialogRef = React.useRef(null);
  const { t } = useTranslation(["campaign", "common"]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState({
    save: false,
    coverImage: false,
    instructionFile: false,
  });
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialInputValues);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [isCoverImageChanged, setIsCoverImageChanged] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState([]);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params?.type === "edit") {
        const data = params.data;
        setInputValues({
          name: data?.name || "",
          type: data?.type || "starter_box",
          instructions: data?.instructions || "",
          coverImage: data?.coverImage || null,
          instructionFile: data?.instructionFile || null,
          maxCreators: data?.maxCreators ?? "",
          commission: data?.commission ?? "",
          kpis: data?.kpis?.length
            ? data.kpis.map((kpi) => {
                const tiers = getKpiTiers(kpi);
                return {
                  platform: kpi.platform,
                  tiers: tiers.length
                    ? tiers.map((tier) => ({
                        minViews: tier.minViews,
                        maxViews: tier.maxViews,
                        timeFrameDays: tier.timeFrameDays,
                        budget: tier.budget ?? "",
                      }))
                    : [{ ...emptyTier }],
                };
              })
            : [{ ...emptyKpi }],
        });
        setSelectedPackages(data?.packages || []);
      }
    },

    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    if (inputValues.coverImage?.startsWith("blob:")) {
      URL.revokeObjectURL(inputValues.coverImage);
    }
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
    setCoverImageFile(null);
    setIsCoverImageChanged(false);
    setSelectedPackages([]);
  };

  const handleChangeInputs = (e) => {
    const { name, value } = e.target;
    setInputValues((prev) => ({ ...prev, [name]: value }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleChangeKpi = (index, name, value) => {
    setInputValues((prev) => ({
      ...prev,
      kpis: prev.kpis.map((kpi, i) =>
        i === index ? { ...kpi, [name]: value } : kpi,
      ),
    }));
    setInputError((prev) => {
      const kpis = [...(prev.kpis || [])];
      if (kpis[index]) kpis[index] = { ...kpis[index], [name]: "" };
      return { ...prev, kpis, kpisGeneral: "" };
    });
  };

  const handleChangeTier = (kpiIndex, tierIndex, name, value) => {
    setInputValues((prev) => ({
      ...prev,
      kpis: prev.kpis.map((kpi, i) =>
        i === kpiIndex
          ? {
              ...kpi,
              tiers: kpi.tiers.map((tier, j) =>
                j === tierIndex ? { ...tier, [name]: value } : tier,
              ),
            }
          : kpi,
      ),
    }));
    setInputError((prev) => {
      const kpis = [...(prev.kpis || [])];
      const tiers = [...(kpis[kpiIndex]?.tiers || [])];
      if (tiers[tierIndex]) tiers[tierIndex] = { ...tiers[tierIndex], [name]: "" };
      if (kpis[kpiIndex]) kpis[kpiIndex] = { ...kpis[kpiIndex], tiers };
      return { ...prev, kpis };
    });
  };

  const handleAddTier = (kpiIndex) => {
    setInputValues((prev) => ({
      ...prev,
      kpis: prev.kpis.map((kpi, i) =>
        i === kpiIndex ? { ...kpi, tiers: [...kpi.tiers, { ...emptyTier }] } : kpi,
      ),
    }));
    setInputError((prev) => {
      const kpis = [...(prev.kpis || [])];
      if (kpis[kpiIndex]) kpis[kpiIndex] = { ...kpis[kpiIndex], tiersGeneral: "" };
      return { ...prev, kpis };
    });
  };

  const handleRemoveTier = (kpiIndex, tierIndex) => {
    setInputValues((prev) => ({
      ...prev,
      kpis: prev.kpis.map((kpi, i) =>
        i === kpiIndex
          ? { ...kpi, tiers: kpi.tiers.filter((_, j) => j !== tierIndex) }
          : kpi,
      ),
    }));
    setInputError((prev) => {
      const kpis = [...(prev.kpis || [])];
      if (kpis[kpiIndex]?.tiers) {
        kpis[kpiIndex] = {
          ...kpis[kpiIndex],
          tiers: kpis[kpiIndex].tiers.filter((_, j) => j !== tierIndex),
        };
      }
      return { ...prev, kpis };
    });
  };

  const handleAddKpi = () => {
    setInputValues((prev) => ({
      ...prev,
      kpis: [...prev.kpis, { ...emptyKpi }],
    }));
    setInputError((prev) => ({ ...prev, kpisGeneral: "" }));
  };

  const handleRemoveKpi = (index) => {
    setInputValues((prev) => ({
      ...prev,
      kpis: prev.kpis.filter((_, i) => i !== index),
    }));
    setInputError((prev) => ({
      ...prev,
      kpis: (prev.kpis || []).filter((_, i) => i !== index),
      kpisGeneral: "",
    }));
  };

  const handleChangePackages = (updater) => {
    setSelectedPackages(updater);
    setInputError((prev) => ({ ...prev, packages: "" }));
  };

  const handleRemovePackage = (pkg) => {
    setSelectedPackages((prev) => prev.filter((item) => item._id !== pkg._id));
  };

  // PDF select hote hi upload ho jati hai
  const handleUploadInstructionFile = async (event) => {
    const file = event.target.files[0];
    instructionFileRef.current.value = null;
    if (!file) return;

    if (file.type !== "application/pdf") {
      return handleOpenSnackBar("error", t("validation.onlyPdfAllowed"));
    }
    if (file.size > MAX_PDF_SIZE) {
      return handleOpenSnackBar("error", t("validation.pdfSizeLimit"));
    }

    try {
      setIsLoading((prev) => ({ ...prev, instructionFile: true }));
      const formData = new FormData();
      formData.append("document", file);

      const response = await axios({
        method: "post",
        url: `${baseUrl}document/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!response?.data?.url) throw new Error("Upload failed");

      setInputValues((prev) => ({
        ...prev,
        instructionFile: {
          url: response.data.url,
          name: file.name,
          size: file.size,
        },
      }));
    } catch (error) {
      handleOpenSnackBar("error", t("pdfUploadFailed"));
    } finally {
      setIsLoading((prev) => ({ ...prev, instructionFile: false }));
    }
  };

  const handleUploadImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (
      !["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(
        file.type,
      )
    ) {
      handleOpenSnackBar(
        "error",
        t("error.invalidImageFormat", { ns: "common" }),
      );
      event.target.value = null;
      return;
    }

    if (inputValues.coverImage?.startsWith("blob:")) {
      URL.revokeObjectURL(inputValues.coverImage);
    }

    setCoverImageFile(file);
    setIsCoverImageChanged(true);
    setInputValues((prev) => ({
      ...prev,
      coverImage: URL.createObjectURL(file),
    }));
    coverImageRef.current.value = null;
  };

  const handleRemoveCoverImage = () => {
    if (inputValues.coverImage?.startsWith("blob:")) {
      URL.revokeObjectURL(inputValues.coverImage);
    }
    setInputValues((prev) => ({ ...prev, coverImage: null }));
    setCoverImageFile(null);
    setIsCoverImageChanged(true);
  };

  const uploadCoverImageIfNeeded = async () => {
    if (!isCoverImageChanged) return inputValues.coverImage;
    if (!coverImageFile) return null;

    try {
      setIsLoading((prev) => ({ ...prev, coverImage: true }));
      const formData = new FormData();
      formData.append("image", coverImageFile);

      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response?.data?.url;
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.imageUploadFailed", { ns: "common" }),
      );
      throw error;
    } finally {
      setIsLoading((prev) => ({ ...prev, coverImage: false }));
    }
  };

  const handleAddEdit = async () => {
    const invalid = validateCampaign(
      inputValues,
      selectedPackages,
      setInputError,
    );
    if (invalid) {
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }

    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const coverImageUrl = await uploadCoverImageIfNeeded();

      const payload = {
        name: inputValues.name.trim(),
        type: inputValues.type,
        packages: selectedPackages.map((pkg) => pkg._id),
        instructions: inputValues.instructions,
        instructionFile: inputValues.instructionFile,
        coverImage: coverImageUrl || null,
        commission: Number(inputValues.commission),
        maxCreators:
          inputValues.maxCreators === "" ? null : Number(inputValues.maxCreators),
        kpis: inputValues.kpis.map((kpi) => ({
          platform: kpi.platform,
          tiers: kpi.tiers.map((tier) => ({
            minViews: Number(tier.minViews),
            maxViews: Number(tier.maxViews),
            timeFrameDays: Number(tier.timeFrameDays),
            budget: Number(tier.budget),
          })),
        })),
      };

      if (paramsData?.type === "add") {
        await handleApiCall(createCampaign, payload, "");
      } else {
        await handleApiCall(updateCampaign, payload, paramsData?.data?._id);
      }
    } catch (error) {
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        paramsData?.getData();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.somethingWentWrong", { ns: "common" }),
      );
    }
  };

  const selectedPlatforms = inputValues.kpis.map((k) => k.platform);

  return (
    <React.Fragment>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog onClose={handleClose} open={open}>
        <DialogHeader
          title={
            paramsData?.type === "add" ? t("addCampaign") : t("editCampaign")
          }
          secondaryHeading={
            paramsData?.type === "add"
              ? t("addCampaignDescription")
              : t("editCampaignDescription")
          }
          icon={<CampaignOutlinedIcon />}
          onClose={handleClose}
        />

        <DialogBody>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <UploadBox
                inputRef={coverImageRef}
                label={t("coverImage")}
                note={t("coverImageNote")}
                value={inputValues.coverImage}
                loading={isLoading.coverImage}
                placeholderImage={APP_IMAGES.uploadImage}
                accept="image/png, image/jpeg ,image/jpg ,image/webp"
                height={{ xs: 200, md: 160 }}
                previewSize={{ xs: 160, md: 180 }}
                onClick={() => coverImageRef.current.click()}
                onChange={handleUploadImage}
                onRemove={handleRemoveCoverImage}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("campaignName")}
                htmlFor={"campaign-name"}
              />
              <TextInput
                id={"campaign-name"}
                required
                name={"name"}
                placeholder={t("enterCampaignName")}
                textLength={150}
                value={inputValues.name}
                onChange={handleChangeInputs}
                inputError={Boolean(inputError.name)}
                helperText={t(inputError.name || "")}
                styleProps={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("campaignType")}
                htmlFor={"campaign-type"}
              />
              <Select
                id={"campaign-type"}
                name={"type"}
                size="small"
                fullWidth
                displayEmpty
                value={inputValues.type}
                onChange={handleChangeInputs}
                error={Boolean(inputError.type)}
              >
                <MenuItem value="" disabled>
                  {t("selectCampaignType")}
                </MenuItem>
                {CAMPAIGN_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(type)}
                  </MenuItem>
                ))}
              </Select>
              {inputError.type && (
                <FormHelperText error>{t(inputError.type)}</FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Label labelText={t("maxCreators")} htmlFor={"max-creators"} />
              <TextInput
                id={"max-creators"}
                type="number"
                name={"maxCreators"}
                placeholder={t("enterMaxCreators")}
                value={inputValues.maxCreators}
                onChange={handleChangeInputs}
                inputError={Boolean(inputError.maxCreators)}
                helperText={t(inputError.maxCreators || "maxCreatorsNote")}
                styleProps={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("commission")}
                htmlFor={"commission"}
              />
              <TextInput
                id={"commission"}
                type="number"
                name={"commission"}
                placeholder={"0 - 100"}
                value={inputValues.commission}
                onChange={handleChangeInputs}
                inputError={Boolean(inputError.commission)}
                helperText={t(inputError.commission || "commissionNote")}
                InputEndIcon={<Typography variant="body2">%</Typography>}
                styleProps={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12}>
              <Label labelText={t("instructions")} htmlFor={"instructions"} />
              <TextInput
                id={"instructions"}
                name={"instructions"}
                placeholder={t("enterInstructions")}
                value={inputValues.instructions}
                onChange={handleChangeInputs}
                multiline
                rows={4}
                styleProps={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12}>
              <Label labelText={t("instructionFile")} />
              <input
                ref={instructionFileRef}
                type="file"
                accept="application/pdf"
                hidden
                onChange={handleUploadInstructionFile}
              />
              {inputValues.instructionFile ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  border="1px solid #d7d7d7"
                  borderRadius={2}
                  p={1.5}
                >
                  <PictureAsPdfOutlinedIcon color="error" />
                  <Stack flex={1} minWidth={0}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      component="a"
                      href={inputValues.instructionFile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "inherit",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {inputValues.instructionFile.name || "instructions.pdf"}
                    </Typography>
                    {inputValues.instructionFile.size > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {(inputValues.instructionFile.size / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                    )}
                  </Stack>
                  <Button
                    size="small"
                    disabled={isLoading.instructionFile}
                    onClick={() => instructionFileRef.current.click()}
                  >
                    {t("replace")}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setInputValues((prev) => ({
                        ...prev,
                        instructionFile: null,
                      }))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  </IconButton>
                </Stack>
              ) : (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={
                    isLoading.instructionFile ? (
                      <CircularProgress size={16} />
                    ) : (
                      <UploadFileOutlinedIcon />
                    )
                  }
                  disabled={isLoading.instructionFile}
                  onClick={() => instructionFileRef.current.click()}
                  sx={{ borderStyle: "dashed", py: 1.5 }}
                >
                  {t("uploadPdf")}
                </Button>
              )}
              <FormHelperText>{t("instructionFileNote")}</FormHelperText>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Label
                  required
                  labelText={`${t("starterBoxes")} (${selectedPackages.length})`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => starterBoxDialogRef.current.openDialog()}
                >
                  {t("addStarterBox")}
                </Button>
              </Stack>
              {inputError.packages && (
                <FormHelperText error>{t(inputError.packages)}</FormHelperText>
              )}
              {selectedPackages.length > 0 ? (
                <Grid container spacing={1.5} mt={0.5}>
                  {selectedPackages.map((pkg) => (
                    <Grid item xs={12} sm={6} key={pkg._id}>
                      <PackageCard
                        cardData={pkg}
                        showRemove
                        onRemove={handleRemovePackage}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  border="1px dashed #d7d7d7"
                  borderRadius={2}
                  py={3}
                  mt={1}
                >
                  {t("noStarterBoxSelected")}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Label
                  required
                  labelText={t("kpis")}
                  labelProps={{ marginBottom: 0 }}
                />
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddKpi}
                  disabled={inputValues.kpis.length >= PLATFORMS.length}
                >
                  {t("addKpi")}
                </Button>
              </Stack>
              {inputError.kpisGeneral && (
                <FormHelperText error>
                  {t(inputError.kpisGeneral)}
                </FormHelperText>
              )}
            </Grid>

            {inputValues.kpis.map((kpi, index) => {
              const kpiError = inputError.kpis?.[index] || {};
              return (
                <Grid item xs={12} key={index}>
                  <Stack
                    border="1px solid #d7d7d7"
                    borderRadius={2}
                    p={1.5}
                    gap={1}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" fontWeight={600}>
                        KPI {index + 1}
                      </Typography>
                      {inputValues.kpis.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveKpi(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      )}
                    </Stack>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <Label required labelText={t("platform")} />
                        <Select
                          size="small"
                          fullWidth
                          displayEmpty
                          value={kpi.platform}
                          onChange={(e) =>
                            handleChangeKpi(index, "platform", e.target.value)
                          }
                          error={Boolean(kpiError.platform)}
                        >
                          <MenuItem value="" disabled>
                            {t("selectPlatform")}
                          </MenuItem>
                          {PLATFORMS.map((platform) => (
                            <MenuItem
                              key={platform}
                              value={platform}
                              disabled={
                                platform !== kpi.platform &&
                                selectedPlatforms.includes(platform)
                              }
                            >
                              {t(platform)}
                            </MenuItem>
                          ))}
                        </Select>
                        {kpiError.platform && (
                          <FormHelperText error>
                            {t(kpiError.platform)}
                          </FormHelperText>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {t("viewRanges")}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddTier(index)}
                          >
                            {t("addRange")}
                          </Button>
                        </Stack>
                        {kpiError.tiersGeneral && (
                          <FormHelperText error>
                            {t(kpiError.tiersGeneral)}
                          </FormHelperText>
                        )}
                      </Grid>
                      {kpi.tiers.map((tier, tierIndex) => {
                        const tierError = kpiError.tiers?.[tierIndex] || {};
                        return (
                          <Grid item xs={12} key={tierIndex}>
                            <Stack
                              bgcolor="#fafafa"
                              border="1px dashed #d7d7d7"
                              borderRadius={2}
                              p={1.25}
                              gap={0.5}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color="text.secondary"
                                >
                                  {t("rangeNumber", { number: tierIndex + 1 })}
                                </Typography>
                                {kpi.tiers.length > 1 && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleRemoveTier(index, tierIndex)
                                    }
                                  >
                                    <DeleteOutlineIcon
                                      fontSize="small"
                                      color="error"
                                    />
                                  </IconButton>
                                )}
                              </Stack>
                              <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6} md={3}>
                              <Label required labelText={t("minViews")} />
                              <TextInput
                                type="number"
                                value={tier.minViews}
                                placeholder="0"
                                onChange={(e) =>
                                  handleChangeTier(
                                    index,
                                    tierIndex,
                                    "minViews",
                                    e.target.value,
                                  )
                                }
                                inputError={Boolean(tierError.minViews)}
                                helperText={t(tierError.minViews || "")}
                                styleProps={{ width: "100%" }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Label required labelText={t("maxViews")} />
                              <TextInput
                                type="number"
                                value={tier.maxViews}
                                placeholder="0"
                                onChange={(e) =>
                                  handleChangeTier(
                                    index,
                                    tierIndex,
                                    "maxViews",
                                    e.target.value,
                                  )
                                }
                                inputError={Boolean(tierError.maxViews)}
                                helperText={t(tierError.maxViews || "")}
                                styleProps={{ width: "100%" }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Label required labelText={t("budget")} />
                              <TextInput
                                type="number"
                                value={tier.budget}
                                placeholder="0"
                                onChange={(e) =>
                                  handleChangeTier(
                                    index,
                                    tierIndex,
                                    "budget",
                                    e.target.value,
                                  )
                                }
                                inputError={Boolean(tierError.budget)}
                                helperText={t(tierError.budget || "")}
                                InputEndIcon={
                                  <Typography variant="body2">RON</Typography>
                                }
                                styleProps={{ width: "100%" }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Label required labelText={t("timeFrameDays")} />
                              <TextInput
                                type="number"
                                value={tier.timeFrameDays}
                                placeholder="1"
                                onChange={(e) =>
                                  handleChangeTier(
                                    index,
                                    tierIndex,
                                    "timeFrameDays",
                                    e.target.value,
                                  )
                                }
                                inputError={Boolean(tierError.timeFrameDays)}
                                helperText={t(tierError.timeFrameDays || "")}
                                styleProps={{ width: "100%" }}
                              />
                            </Grid>
                              </Grid>
                            </Stack>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </DialogBody>

        <DialogActionButtons
          onConfirm={handleAddEdit}
          onCancel={handleClose}
          confirmText="save"
          cancelText="cancel"
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />
      </BootstrapDialog>
      <AttachedBasePackageDialog
        ref={starterBoxDialogRef}
        selectedBasePackages={selectedPackages}
        setSelectedBasePackages={handleChangePackages}
        title={t("starterBoxes")}
        subtitle={t("selectStarterBoxes")}
      />
    </React.Fragment>
  );
});

export default AddEditCampaign;
